import type { InvoiceBuyer, InvoiceBuyerInput } from '@/lib/api/sales-order-api'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '@/components/ui/select'
import { m } from '@/lib/paraglide/messages'

// InvoiceBuyerType chọn dòng người mua trên hóa đơn MISA:
//   - 'consumer': bán cho người tiêu dùng — chỉ ghi "Họ tên người mua hàng",
//     các ô còn lại (tên đơn vị, MST, địa chỉ, email) để trống.
//   - 'company': khách lấy hóa đơn công ty — cần tên đơn vị + MST để khấu trừ.
export type InvoiceBuyerType = 'consumer' | 'company'

// InvoiceBuyerFormValue is the form-local state for the invoice buyer block.
// `enabled` mirrors the "Xuất hóa đơn điện tử cho đơn này" checkbox — it is the
// GATE (off = no invoice at all), not just a "show buyer fields" toggle.
// `buyerType` chọn bộ ô hiện ra; các ô công ty vẫn giữ giá trị khi đổi qua lại
// để nhân viên chọn nhầm không phải gõ lại (payload chỉ gửi ô của loại đang chọn).
export interface InvoiceBuyerFormValue {
    enabled: boolean
    buyerType: InvoiceBuyerType
    buyerName: string
    taxCode: string
    address: string
    email: string
}

export const EMPTY_INVOICE_BUYER: InvoiceBuyerFormValue = {
    enabled: false,
    buyerType: 'consumer',
    buyerName: '',
    taxCode: '',
    address: '',
    email: '',
}

// DEFAULT_CREATE_INVOICE_BUYER seeds the CREATE forms (M-7): the tick starts
// OFF — phần lớn đơn bán lẻ KHÔNG xuất hóa đơn, nên đơn mới mặc định không gửi
// gì sang MISA (`issue_einvoice=false` ⇒ đơn không vào nhóm "Chờ xuất hóa đơn").
// Nhân viên tự tick khi khách yêu cầu hóa đơn. Loại người mua mặc định vẫn là
// NGƯỜI TIÊU DÙNG, chỉ đổi sang "Công ty" khi khách cần khấu trừ. Edit forms
// keep the order's stored flag instead.
export const DEFAULT_CREATE_INVOICE_BUYER: InvoiceBuyerFormValue = {
    ...EMPTY_INVOICE_BUYER,
    enabled: false,
}

// isLikelyEmail is a soft check — empty is fine (không bắt buộc), and a rough
// shape check only drives a REMINDER, never blocks submit.
export function isLikelyEmail(value: string): boolean {
    const trimmed = value.trim()
    if (trimmed === '') return true
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmed)
}

// buildInvoiceBuyerPayload maps the form value to the API's invoice_buyer field.
//   - create + not enabled → undefined (không gửi ⇒ khách lẻ).
//   - edit   + not enabled → {} (xóa dòng ⇒ bỏ tick; backend clears).
//   - người tiêu dùng → CHỈ họ tên: MST/địa chỉ/email của lần chọn "công ty"
//     trước đó không được đi kèm, hóa đơn bán lẻ phải trống các ô đó.
//   - công ty → cả bốn ô, đã trim (rỗng ⇒ bỏ field, không gửi chuỗi rỗng).
export function buildInvoiceBuyerPayload(
    value: InvoiceBuyerFormValue,
    mode: 'create' | 'edit',
): InvoiceBuyerInput | undefined {
    if (!value.enabled) {
        return mode === 'edit' ? {} : undefined
    }
    if (value.buyerType === 'consumer') {
        return {
            buyer_type: 'consumer',
            buyer_name: value.buyerName.trim() || undefined,
        }
    }
    return {
        buyer_type: 'company',
        buyer_name: value.buyerName.trim() || undefined,
        tax_code: value.taxCode.trim() || undefined,
        address: value.address.trim() || undefined,
        email: value.email.trim() || undefined,
    }
}

// CustomerBuyerSource is the subset of a customer used to auto-fill the invoice
// buyer block — the fields a company customer has on file.
export interface CustomerBuyerSource {
    company?: string
    name?: string
    tax_code?: string
    address?: string
    email?: string
}

// customerBuyerName picks the company name to put on the invoice: the dedicated
// `company` field, or fall back to the customer's `name` (some records store the
// company name there). Staff can still edit it.
export function customerBuyerName(c: CustomerBuyerSource): string {
    return c.company?.trim() || c.name?.trim() || ''
}

// applyCustomerToBuyer fills EMPTY buyer fields from the customer on file (MST...)
// so staff don't retype them. Fields the user already typed are kept — auto-fill
// only suggests, never overwrites a manual edit.
export function applyCustomerToBuyer(
    value: InvoiceBuyerFormValue,
    customer?: CustomerBuyerSource | null,
): InvoiceBuyerFormValue {
    if (!customer) return value
    return {
        ...value,
        buyerName: value.buyerName || customerBuyerName(customer),
        taxCode: value.taxCode || customer.tax_code?.trim() || '',
        address: value.address || customer.address?.trim() || '',
        email: value.email || customer.email?.trim() || '',
    }
}

// invoiceBuyerToFormValue prefills the form from a fetched order. The tick state
// comes from the order's issue_einvoice flag (a retail order can be ticked with
// no company row); the company fields come from the buyer row when present. Falls
// back to buyer-existence for legacy orders that predate the flag.
export function invoiceBuyerToFormValue(
    buyer?: InvoiceBuyer | null,
    issueEInvoice?: boolean,
): InvoiceBuyerFormValue {
    return {
        enabled: issueEInvoice ?? Boolean(buyer),
        // Dòng buyer cũ (trước khi có buyer_type) mang chuỗi rỗng: mở lại ở chế
        // độ công ty, đúng nghĩa lúc nó được tạo. Không có dòng ⇒ người tiêu dùng.
        buyerType: buyerTypeOf(buyer),
        buyerName: buyer?.buyer_name ?? '',
        taxCode: buyer?.tax_code ?? '',
        address: buyer?.address ?? '',
        email: buyer?.email ?? '',
    }
}

function buyerTypeOf(buyer?: InvoiceBuyer | null): InvoiceBuyerType {
    if (!buyer) return 'consumer'
    return buyer.buyer_type === 'consumer' ? 'consumer' : 'company'
}

// isCompanyProfile lọc hồ sơ khách đáng đổ vào khối hóa đơn CÔNG TY: phải có
// tên công ty hoặc MST. Khách lẻ mặc định của hệ thống chỉ có tên + địa chỉ, và
// địa chỉ đó là chuỗi rác ("Khách vãng lai") — đổ nó vào là ghi sai địa chỉ
// người mua trên chứng từ.
function isCompanyProfile(customer?: CustomerBuyerSource | null): boolean {
    if (!customer) return false
    return Boolean(customer.company?.trim() || customer.tax_code?.trim())
}

// applyBuyerPatch gộp patch từ khối người mua vào state của form và tự đổ hồ sơ
// công ty của khách khi — và chỉ khi — người dùng chuyển sang hóa đơn CÔNG TY
// (hoặc bật tick trong lúc đang ở chế độ công ty) VÀ khách đó là pháp nhân. Chế
// độ người tiêu dùng không có ô MST/địa chỉ nên không được đổ dữ liệu công ty
// vào sau lưng người dùng.
//
// Đổi loại thì XÓA ô tên: nó dùng chung cho hai nghĩa khác nhau ("Họ tên người
// mua" vs "Tên công ty"). Giữ lại thì tên cá nhân nằm nguyên trong ô Tên công ty
// và còn chặn auto-fill điền tên công ty thật (auto-fill chỉ đụng ô trống).
export function applyBuyerPatch(
    prev: InvoiceBuyerFormValue,
    patch: Partial<InvoiceBuyerFormValue>,
    customer?: CustomerBuyerSource | null,
): InvoiceBuyerFormValue {
    const changedType =
        patch.buyerType !== undefined && patch.buyerType !== prev.buyerType
    const next = {
        ...prev,
        ...patch,
        ...(changedType ? { buyerName: '' } : {}),
    }
    const switchedToCompany = patch.buyerType === 'company'
    const enabledOnCompany =
        patch.enabled === true && next.buyerType === 'company'
    return (switchedToCompany || enabledOnCompany) && isCompanyProfile(customer)
        ? applyCustomerToBuyer(next, customer)
        : next
}

interface Props {
    value: InvoiceBuyerFormValue
    onChange: (patch: Partial<InvoiceBuyerFormValue>) => void
    // idPrefix keeps ids unique — the create screen mounts desktop + mobile forms
    // together (responsive show/hide).
    idPrefix: string
    /** POS "Bán ngay": không kèm dòng mô tả dưới ô tích (M-167). */
    counterSale?: boolean
}

// InvoiceBuyerFields renders the "Xuất hóa đơn điện tử cho đơn này" checkbox and, when
// checked, the buyer info inputs. Fully controlled so it drops into both the
// create form (react-hook-form) and the edit form (useState). The caller gates
// visibility behind the `dev-einvoice` flag; this component only renders the UI.
// Nothing here blocks submit — reminders are advisory text (chỉ nhắc).
export function InvoiceBuyerFields({
    value,
    onChange,
    idPrefix,
    counterSale = false,
}: Props) {
    const isConsumer = value.buyerType === 'consumer'
    const showTaxReminder = value.taxCode.trim() === ''
    const showEmailReminder = !isLikelyEmail(value.email)

    return (
        <div className="space-y-3 rounded-lg border bg-card p-3">
            <label
                htmlFor={`${idPrefix}-invoice-buyer-toggle`}
                className="flex cursor-pointer items-center gap-2 text-sm font-medium"
            >
                <input
                    type="checkbox"
                    id={`${idPrefix}-invoice-buyer-toggle`}
                    checked={value.enabled}
                    onChange={(e) => onChange({ enabled: e.target.checked })}
                    className="size-4 accent-primary"
                />
                {m.order_form_invoice_buyer_toggle()}
            </label>
            {/* POS Bán ngay: owner bỏ hẳn dòng mô tả dưới ô tích (M-167). */}
            {!counterSale && (
                <p className="text-xs text-muted-foreground">
                    {m.order_form_invoice_buyer_hint()}
                </p>
            )}

            {value.enabled && (
                <div className="space-y-3 pt-1">
                    <div className="space-y-1.5">
                        <Label htmlFor={`${idPrefix}-invoice-buyer-type`}>
                            {m.order_form_invoice_buyer_type()}
                        </Label>
                        <Select
                            value={value.buyerType}
                            onValueChange={(next) =>
                                onChange({
                                    buyerType: next as InvoiceBuyerType,
                                })
                            }
                        >
                            <SelectTrigger
                                id={`${idPrefix}-invoice-buyer-type`}
                                className="w-full"
                            >
                                <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value="consumer">
                                    {m.order_form_invoice_buyer_type_consumer()}
                                </SelectItem>
                                <SelectItem value="company">
                                    {m.order_form_invoice_buyer_type_company()}
                                </SelectItem>
                            </SelectContent>
                        </Select>
                        <p className="text-xs text-muted-foreground">
                            {isConsumer
                                ? m.order_form_invoice_buyer_consumer_hint()
                                : m.order_form_invoice_buyer_company_hint()}
                        </p>
                    </div>

                    <div className="space-y-1.5">
                        <Label htmlFor={`${idPrefix}-invoice-buyer-name`}>
                            {isConsumer
                                ? m.order_form_invoice_buyer_consumer_name()
                                : m.order_form_invoice_buyer_name()}
                        </Label>
                        <Input
                            id={`${idPrefix}-invoice-buyer-name`}
                            value={value.buyerName}
                            onChange={(e) =>
                                onChange({ buyerName: e.target.value })
                            }
                            placeholder={
                                isConsumer
                                    ? m.order_form_invoice_buyer_consumer_name_ph()
                                    : m.order_form_invoice_buyer_name_ph()
                            }
                            maxLength={255}
                        />
                    </div>
                </div>
            )}

            {value.enabled && !isConsumer && (
                <div className="space-y-3">
                    <div className="space-y-1.5">
                        <Label htmlFor={`${idPrefix}-invoice-buyer-tax`}>
                            {m.order_form_invoice_buyer_tax_code()}
                        </Label>
                        <Input
                            id={`${idPrefix}-invoice-buyer-tax`}
                            value={value.taxCode}
                            onChange={(e) =>
                                onChange({ taxCode: e.target.value })
                            }
                            placeholder={m.order_form_invoice_buyer_tax_code_ph()}
                            maxLength={20}
                            inputMode="numeric"
                        />
                        {showTaxReminder && (
                            <p className="text-xs text-amber-600 dark:text-amber-500">
                                {m.order_form_invoice_buyer_tax_code_reminder()}
                            </p>
                        )}
                    </div>

                    <div className="space-y-1.5">
                        <Label htmlFor={`${idPrefix}-invoice-buyer-address`}>
                            {m.order_form_invoice_buyer_address()}
                        </Label>
                        <Input
                            id={`${idPrefix}-invoice-buyer-address`}
                            value={value.address}
                            onChange={(e) =>
                                onChange({ address: e.target.value })
                            }
                            placeholder={m.order_form_invoice_buyer_address_ph()}
                            maxLength={500}
                        />
                    </div>

                    <div className="space-y-1.5">
                        <Label htmlFor={`${idPrefix}-invoice-buyer-email`}>
                            {m.order_form_invoice_buyer_email()}
                        </Label>
                        <Input
                            id={`${idPrefix}-invoice-buyer-email`}
                            value={value.email}
                            onChange={(e) =>
                                onChange({ email: e.target.value })
                            }
                            placeholder={m.order_form_invoice_buyer_email_ph()}
                            maxLength={255}
                            inputMode="email"
                        />
                        {showEmailReminder && (
                            <p className="text-xs text-amber-600 dark:text-amber-500">
                                {m.order_form_invoice_buyer_email_reminder()}
                            </p>
                        )}
                    </div>
                </div>
            )}
        </div>
    )
}
